import React, { useCallback, useEffect, useState } from "react";
import api from "utils/api";
import { formatEuro } from "scenes/team/formatters";

const PERIODS = [
  { id: "day", title: "Tagesziel", timeframe: "Heute" },
  { id: "week", title: "Wochenziel", timeframe: "Diese Woche · Mo–So" },
  { id: "month", title: "Monatsziel", timeframe: "Dieser Monat" },
];

const formatDate = (value) => {
  if (!value) return "";
  const [year, month, day] = value.split("-").map(Number);
  return new Intl.DateTimeFormat("de-DE", {
    day: "numeric", month: "long", year: "numeric",
  }).format(new Date(Date.UTC(year, month - 1, day)));
};

const GoalsTab = () => {
  const [data, setData] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data: payload } = await api.get("/employee/goals");
      setData(payload);
      setStartDate(payload.startDate || "");
    } catch (err) {
      setError(err?.response?.data?.message || "Ziele konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveStartDate = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      const { data: payload } = await api.patch("/employee/goals", { startDate });
      setData(payload);
      setStartDate(payload.startDate || "");
      setSaved(true);
    } catch (err) {
      setError(err?.response?.data?.message || "Startdatum konnte nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="employee-goals" role="tabpanel" aria-label="Ziele">
      <div className="employee-goals-intro">
        <p className="employee-kicker">Dein Fortschritt</p>
        <h1>Ziele</h1>
        <p>Sieh deinen Umsatz und deine Firmenbesuche für heute, diese Woche und diesen Monat.</p>
      </div>

      {loading ? <div className="team-empty" role="status">Ziele werden geladen…</div> : (
        <>
          <section className="employee-goals-start" aria-labelledby="employee-goals-start-title">
            <div>
              <h2 id="employee-goals-start-title">Dein Startdatum</h2>
              <p>In den ersten sechs Monaten gelten die Ziele der Probezeit. Danach wechseln sie automatisch.</p>
            </div>
            <form onSubmit={saveStartDate} className="employee-goals-start-form">
              <label htmlFor="employee-start-date">Startdatum</label>
              <div className="employee-goals-start-controls">
                <input
                  id="employee-start-date"
                  type="date"
                  required
                  max={data?.today || undefined}
                  value={startDate}
                  onChange={(event) => { setStartDate(event.target.value); setSaved(false); }}
                />
                <button type="submit" className="employee-btn" disabled={saving || !startDate || startDate === data?.startDate}>
                  {saving ? "Speichert…" : "Speichern"}
                </button>
              </div>
              {saved && <span className="employee-goals-saved" role="status">Startdatum gespeichert.</span>}
            </form>
          </section>

          {error && <div className="auth-alert is-error" role="alert">{error}</div>}

          {data?.startDate ? (
            <>
              <div className="employee-goals-phase">
                <span className="employee-goals-phase-badge">
                  {data.phase === "probation" ? "Während der Probezeit" : "Nach der Probezeit"}
                </span>
                <span>Start am {formatDate(data.startDate)}</span>
              </div>
              <div className="employee-goals-grid">
                {PERIODS.map(({ id, title, timeframe }) => {
                  const period = data.periods[id];
                  const target = period.targetGrossCents;
                  const current = period.grossCents;
                  const percent = Math.round((current / target) * 100);
                  const remaining = Math.max(0, target - current);
                  const visits = period.visitCount || 0;
                  const visitTarget = period.targetVisits;
                  const visitPercent = Math.round((visits / visitTarget) * 100);
                  return (
                    <section className="employee-goal-card" key={id} aria-labelledby={`employee-goal-${id}`}>
                      <div className="employee-goal-card-top">
                        <div>
                          <span className="employee-goal-timeframe">{timeframe}</span>
                          <h2 id={`employee-goal-${id}`}>{title}</h2>
                        </div>
                        <strong className="employee-goal-percent">{percent} %</strong>
                      </div>
                      <p className="employee-goal-amount">
                        <strong>{formatEuro(current, "de")}</strong>
                        <span>von {formatEuro(target, "de")} Umsatz</span>
                      </p>
                      <div
                        className="employee-goal-progress"
                        role="progressbar"
                        aria-label={`${title} Umsatz`}
                        aria-valuemin="0"
                        aria-valuemax={target}
                        aria-valuenow={Math.min(current, target)}
                        aria-valuetext={`${formatEuro(current, "de")} von ${formatEuro(target, "de")}`}
                      >
                        <span style={{ width: `${Math.min(100, percent)}%` }} />
                      </div>
                      <p className="employee-goal-remaining">
                        {remaining ? `Noch ${formatEuro(remaining, "de")} bis zum Ziel` : "Umsatzziel erreicht!"}
                      </p>
                      <div className="employee-goal-extra">
                        <span>Zusatz-Ziel</span>
                        <strong>{visits.toLocaleString("de-DE")} von {visitTarget.toLocaleString("de-DE")} Firmenbesuchen</strong>
                        <div
                          className="employee-goal-progress employee-goal-visit-progress"
                          role="progressbar"
                          aria-label={`${title} Firmenbesuche`}
                          aria-valuemin="0"
                          aria-valuemax={visitTarget}
                          aria-valuenow={Math.min(visits, visitTarget)}
                          aria-valuetext={`${visits} von ${visitTarget} Firmenbesuchen`}
                        >
                          <span style={{ width: `${Math.min(100, visitPercent)}%` }} />
                        </div>
                        <small>
                          {visits >= visitTarget
                            ? "Besuchsziel erreicht!"
                            : `Noch ${(visitTarget - visits).toLocaleString("de-DE")} Firmenbesuche bis zum Ziel`}
                        </small>
                      </div>
                    </section>
                  );
                })}
              </div>
              <p className="employee-goals-note">
                Der Umsatz stammt aus deinen Einträgen im Umsatz-Tab.
                Jede zugeordnete GO-Nachricht zählt am dort angegebenen Datum als ein Firmenbesuch,
                auch ohne Umsatzbetrag. Umsatz und Besuche vor deinem Startdatum zählen nicht mit.
              </p>
            </>
          ) : (
            <p className="employee-goals-prompt">Speichere dein Startdatum, um deine Ziele und deinen Fortschritt zu sehen.</p>
          )}
        </>
      )}
    </div>
  );
};

export default GoalsTab;
